import * as React from "react"
import * as ReactDOM from "react-dom"
import { useLocation } from "@gatsbyjs/reach-router"
import { AudioLines } from "lucide-react"

import { useMotionPreference } from "../utils/motion-preference"

const POLL_INTERVAL = 5_000
const ENTER_DURATION = 820
const LEAVE_DURATION = 780
const useBrowserLayoutEffect =
  typeof window === "undefined" ? React.useEffect : React.useLayoutEffect

const normalizePath = path => {
  if (!path || path === "/") return "/"
  return path.replace(/\/+$/, "")
}

function formatMs(ms) {
  if (!ms || ms < 0) return "0:00"
  const totalSeconds = Math.floor(ms / 1000)
  const m = Math.floor(totalSeconds / 60)
  const s = totalSeconds % 60
  return `${m}:${s.toString().padStart(2, "0")}`
}

const NowPlaying = () => {
  const location = useLocation()
  const { reduceMotion } = useMotionPreference()
  const [data, setData] = React.useState(null)
  const [displayData, setDisplayData] = React.useState(null)
  const [phase, setPhaseState] = React.useState("hidden")
  const [localProgress, setLocalProgress] = React.useState(0)
  const chromeRef = React.useRef(null)
  const pillRef = React.useRef(null)
  const pillBlobRef = React.useRef(null)
  const dockBlobRef = React.useRef(null)
  const tickRef = React.useRef(null)
  const lastPollRef = React.useRef(0)
  const endRefreshTriggeredRef = React.useRef(false)
  const phaseRef = React.useRef("hidden")
  const enterTimerRef = React.useRef(null)
  const leaveTimerRef = React.useRef(null)
  const pathname = normalizePath(location.pathname)
  const isMusicRoute = pathname === "/music" || pathname.startsWith("/music/")

  const setPhase = React.useCallback(nextPhase => {
    phaseRef.current = nextPhase
    setPhaseState(nextPhase)
  }, [])

  const clearMotionTimers = React.useCallback(() => {
    if (enterTimerRef.current) {
      window.clearTimeout(enterTimerRef.current)
      enterTimerRef.current = null
    }

    if (leaveTimerRef.current) {
      window.clearTimeout(leaveTimerRef.current)
      leaveTimerRef.current = null
    }
  }, [])

  const poll = React.useCallback(() => {
    fetch("/api/now-playing")
      .then(r => r.json())
      .then(d => {
        setData(d)
        const durationMs = d.durationMs ?? 0
        const progressMs = d.progressMs ?? 0

        if (!d.playing || !durationMs || progressMs < durationMs) {
          endRefreshTriggeredRef.current = false
        }

        if (d.playing) {
          setLocalProgress(progressMs)
          lastPollRef.current = Date.now()
        }
      })
      .catch(() => {
        setData(null)
      })
  }, [])

  React.useEffect(() => {
    if (!isMusicRoute) return undefined

    poll()
    const id = setInterval(poll, POLL_INTERVAL)
    return () => clearInterval(id)
  }, [isMusicRoute, poll])

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined
    return () => clearMotionTimers()
  }, [clearMotionTimers])

  React.useEffect(() => {
    if (typeof window === "undefined") return

    const shouldShow = isMusicRoute && !!data?.playing

    if (shouldShow) {
      setDisplayData(data)

      if (reduceMotion) {
        clearMotionTimers()
        setPhase("idle")
        return
      }

      if (leaveTimerRef.current) {
        window.clearTimeout(leaveTimerRef.current)
        leaveTimerRef.current = null
      }

      if (phaseRef.current === "hidden" || phaseRef.current === "leaving") {
        if (enterTimerRef.current) {
          window.clearTimeout(enterTimerRef.current)
          enterTimerRef.current = null
        }

        setPhase("entering")
        enterTimerRef.current = window.setTimeout(() => {
          enterTimerRef.current = null
          setPhase("idle")
        }, ENTER_DURATION)
      }

      return
    }

    if (phaseRef.current === "hidden") return

    clearMotionTimers()

    if (reduceMotion) {
      setPhase("hidden")
      setDisplayData(null)
      return
    }

    setPhase("leaving")
    leaveTimerRef.current = window.setTimeout(() => {
      leaveTimerRef.current = null
      setPhase("hidden")
      setDisplayData(null)
    }, LEAVE_DURATION)
  }, [clearMotionTimers, data, isMusicRoute, reduceMotion, setPhase])

  React.useEffect(() => {
    if (phase === "hidden" || !displayData?.playing) {
      if (tickRef.current) {
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
      return
    }

    const baseProgress = displayData.progressMs ?? 0
    const durationMs = displayData.durationMs ?? 0

    const step = () => {
      const elapsed = Date.now() - lastPollRef.current
      const nextProgress = baseProgress + elapsed
      const progress = durationMs ? Math.min(nextProgress, durationMs) : nextProgress

      setLocalProgress(progress)

      if (durationMs && nextProgress >= durationMs) {
        if (!endRefreshTriggeredRef.current) {
          endRefreshTriggeredRef.current = true
          poll()
        }
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
    }

    step()
    tickRef.current = window.setInterval(step, 1000)

    return () => {
      if (tickRef.current) {
        window.clearInterval(tickRef.current)
        tickRef.current = null
      }
    }
  }, [phase, displayData, poll])

  const measureGooDistance = React.useCallback(() => {
    const chrome = chromeRef.current
    const pillBlob = pillBlobRef.current
    const dockBlob = dockBlobRef.current
    if (!chrome || !pillBlob || !dockBlob) return

    const pillCenter = pillBlob.offsetLeft + pillBlob.offsetWidth / 2
    const dockCenter = dockBlob.offsetLeft + dockBlob.offsetWidth / 2
    const dx = dockCenter - pillCenter
    chrome.style.setProperty("--now-playing-dx", `${dx}px`)
  }, [])

  const updateLayoutMode = React.useCallback(() => {
    if (typeof document === "undefined" || typeof window === "undefined") return

    const root = document.documentElement
    const chrome = chromeRef.current
    const pill = pillRef.current
    const nav = document.querySelector(".floating-nav")

    if (!chrome || !pill || !nav || phaseRef.current === "hidden") {
      root.classList.remove("is-now-playing-stacked")
      return
    }

    const chromeStyles = window.getComputedStyle(chrome)
    const sidePadding = parseFloat(chromeStyles.paddingLeft) || 14
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth
    const navWidth = nav.getBoundingClientRect().width
    const pillWidth = pill.getBoundingClientRect().width
    const availableLeftGutter = (viewportWidth - navWidth) / 2
    const minimumGap = 16
    const shouldStack = sidePadding + pillWidth + minimumGap > availableLeftGutter

    root.classList.toggle("is-now-playing-stacked", shouldStack)
  }, [])

  useBrowserLayoutEffect(() => {
    if (typeof window === "undefined" || phase === "hidden" || reduceMotion) return undefined

    measureGooDistance()
    window.addEventListener("resize", measureGooDistance)

    return () => {
      window.removeEventListener("resize", measureGooDistance)
    }
  }, [measureGooDistance, phase, reduceMotion])

  useBrowserLayoutEffect(() => {
    if (typeof window === "undefined" || phase === "hidden") {
      if (typeof document !== "undefined") {
        document.documentElement.classList.remove("is-now-playing-stacked")
      }
      return undefined
    }

    updateLayoutMode()
    window.addEventListener("resize", updateLayoutMode)

    const nav = document.querySelector(".floating-nav")
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(updateLayoutMode)

    if (resizeObserver) {
      if (pillRef.current) resizeObserver.observe(pillRef.current)
      if (nav) resizeObserver.observe(nav)
    }

    return () => {
      window.removeEventListener("resize", updateLayoutMode)
      resizeObserver?.disconnect()
      document.documentElement.classList.remove("is-now-playing-stacked")
    }
  }, [phase, updateLayoutMode])

  useBrowserLayoutEffect(() => {
    if (typeof document === "undefined") return undefined

    const root = document.documentElement
    const isRendered = phase !== "hidden" && !!displayData?.playing

    root.style.setProperty("--now-playing-height", isRendered ? "76px" : "0px")
    root.style.setProperty("--now-playing-pill-height", isRendered ? "76px" : "0px")
    root.classList.toggle("has-now-playing-pill", isRendered)
    root.classList.toggle("is-now-playing-entering", phase === "entering")
    root.classList.toggle("is-now-playing-leaving", phase === "leaving")

    return () => {
      root.style.setProperty("--now-playing-height", "0px")
      root.style.setProperty("--now-playing-pill-height", "0px")
      root.classList.remove(
        "has-now-playing-pill",
        "is-now-playing-entering",
        "is-now-playing-leaving",
        "is-now-playing-stacked"
      )
    }
  }, [displayData?.playing, phase])

  if (phase === "hidden" || !displayData?.playing) return null

  const progress = Math.min(localProgress, displayData.durationMs || 1)
  const pct = displayData.durationMs ? (progress / displayData.durationMs) * 100 : 0
  const chromeClassName = [
    "now-playing-chrome",
    phase === "entering" ? "is-entering" : "",
    phase === "leaving" ? "is-leaving" : "",
  ].filter(Boolean).join(" ")

  return ReactDOM.createPortal(
    <div ref={chromeRef} className={chromeClassName} data-glass-exclude>
      <div className="now-playing-chrome__goo" aria-hidden="true">
        <div ref={pillBlobRef} className="now-playing-chrome__blob now-playing-chrome__pill-blob" />
        <div ref={dockBlobRef} className="now-playing-chrome__blob now-playing-chrome__dock-blob" />
        <div />
      </div>

      <div ref={pillRef} className="now-playing-pill" style={{ "--now-playing-progress": pct }}>
        <div className="now-playing-pill__ring now-playing-pill__fx" aria-hidden="true">
          {displayData.albumArt ? (
            <img
              src={displayData.albumArtSmall || displayData.albumArt}
              alt=""
              className="now-playing-pill__art"
            />
          ) : (
            <span className="now-playing-pill__fallback-art">
              <AudioLines size={20} strokeWidth={2.5} aria-hidden="true" />
            </span>
          )}
        </div>

        <div className="now-playing-pill__copy now-playing-pill__fx">
          {displayData.trackUrl ? (
            <a
              href={displayData.trackUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="now-playing-pill__track"
            >
              {displayData.track}
            </a>
          ) : (
            <span className="now-playing-pill__track">{displayData.track}</span>
          )}
          <span className="now-playing-pill__artist">{displayData.artist}</span>
        </div>

        <div className="now-playing-pill__eq now-playing-pill__fx" aria-label="Currently playing">
          <span />
          <span />
          <span />
          <span />
        </div>

        <div className="now-playing-pill__time now-playing-pill__fx">
          <span>{formatMs(progress)}</span>
          <span className="now-playing-pill__time-sep">/</span>
          <span>{formatMs(displayData.durationMs)}</span>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default NowPlaying
